<template>
    <div class="explore-page">
        <!-- 顶部Tab切换区域 -->
        <div class="tab-header">
            <div class="tab-container">
                <button v-for="tab in tabs" :key="tab.value" class="tab-item"
                    :class="{ 'is-active': activeTab === tab.value }" @click="handleTabChange(tab.value)">
                    <!-- Tab图标 -->
                    <span v-if="tab.icon" class="tab-icon" :class="tab.icon"></span>
                    <span class="tab-text">{{ tab.label }}</span>
                    <!-- 激活下划线 -->
                    <div v-if="activeTab === tab.value" class="tab-indicator"></div>
                </button>
            </div>
        </div>

        <!-- 内容区域 - 使用NuxtPage展示子路由 -->
        <div class="content-area">
            <NuxtPage />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, provide } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useRoute, useRouter, definePageMeta } from '#imports'
import { deleteLangPrefix } from '~/utils/common'

// 页面元数据
definePageMeta({
    layout: 'default',
})

// 国际化
const { t } = useI18n()
const route = useRoute()
const router = useRouter()

// Tab配置
const tabs = computed(() => [
    {
        label: t('menus.explorePage.imageInspiration'),
        value: 'image',
        icon: 'i-explore-img-icon'
    },
    {
        label: t('menus.explorePage.videoInspiration'),
        value: 'video',
        icon: 'i-explore-video-icon'
    },
    // {
    //     label: t('menus.digitalHuman'),
    //     value: 'characters',
    //     icon: 'i-explore-ai-icon'
    // },
    {
        label: t('menus.explorePage.musicInspiration'),
        value: 'music',
        icon: 'i-explore-mp3-icon'
    },
])

// 根据当前路由初始化activeTab
const getInitialTab = () => {
    const currentPath = deleteLangPrefix(route.path) || '/'

    if (currentPath.startsWith('/explore/characters')) {
        return 'characters'
    } else {
        // 从query参数中获取type
        const type = route.query.type as string
        if (type === 'video') {
            return 'video'
        } else if (type === 'music') {
            return 'music'
        } else {
            return 'image'
        }
    }
}

// 当前激活的Tab - 根据路由判断
const activeTab = ref(getInitialTab())

// 根据路由更新activeTab
watch(
    () => [route.path, route.query.type] as const,
    ([path, type]) => {
        const currentPath = deleteLangPrefix(path as string) || '/'

        if (currentPath.startsWith('/explore/characters')) {
            activeTab.value = 'characters'
        } else {
            // 根据query参数确定tab
            if (type === 'video') {
                activeTab.value = 'video'
            } else if (type === 'music') {
                activeTab.value = 'music'
            } else {
                activeTab.value = 'image'
            }
        }
    },
    { immediate: true }
)

// Tab切换处理
const handleTabChange = (tabValue: string) => {
    if (activeTab.value === tabValue) return

    activeTab.value = tabValue

    // 根据tab切换路由
    if (tabValue === 'characters') {
        router.push('/explore/characters')
    } else if (tabValue === 'video') {
        router.push('/explore?type=video')
    } else if (tabValue === 'music') {
        router.push('/explore?type=music')
    } else {
        // 图片灵感
        router.push('/explore')
    }
}

// 提供activeTab给子组件
provide('activeTab', activeTab)
</script>

<style scoped lang="less">
.explore-page {
    min-height: 100vh;
    padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px)); // 为底部导航留出空间(包含安全区域)
}

// 顶部Tab区域
.tab-header {
    position: sticky;
    top: 51px; // 固定在顶部
    z-index: 40;
    padding: 10px 0 10px;
    background: #000000;
    box-shadow: 0 1px 0 0 #000000; // 底部阴影覆盖可能的白边
}

.tab-container {
    display: flex;
    align-items: center;
    gap: 4.25px; // Figma设计中的间距
    height: auto;
    padding: 0;
    margin-bottom: 0;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.tab-item {
    position: relative;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5.25px; // 图标和文字之间的间距
    border: 1px solid #27272A;
    cursor: pointer;
    padding: 5.25px 10.5px;
    border-radius: 9999px; // 完全圆角
    background: #18181B;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    -webkit-tap-highlight-color: transparent;

    .tab-icon {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        background-color: #71717A; // 默认灰色
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .tab-text {
        font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.5;
        color: #71717A;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        white-space: nowrap;
        text-align: center;
    }

    &.is-active {
        background: #FFFFFF;
        border-color: #FFFFFF;
        box-shadow:
            0px 4px 6px -4px rgba(0, 0, 0, 0.1),
            0px 10px 15px -3px rgba(0, 0, 0, 0.1);
        padding: 4.96px 11.07px 5.57px;

        .tab-icon {
            background-color: #000000; // 激活状态图标变黑色
        }

        .tab-text {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 11.6px;
            font-weight: 700;
            line-height: 1.493534;
            color: #000000;
        }
    }

    &:not(.is-active):active {
        background: #1f2024;
    }
}

// 移除激活指示器（使用按钮背景色替代）
.tab-indicator {
    display: none;
}

@keyframes slideIn {
    from {
        transform: scaleX(0);
        opacity: 0;
    }

    to {
        transform: scaleX(1);
        opacity: 1;
    }
}

// 内容区域
.content-area {
    width: 100%;
}

// PC端适配
@media (min-width: 768px) {
    .explore-page {
        padding-bottom: 0; // PC端没有底部导航
    }

    .tab-item {
        .tab-text {
            font-size: 14px;
        }
    }
}

// Tab图标样式
.i-explore-img-icon {
    mask: url('~/assets/images/explore/img.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('~/assets/images/explore/img.svg') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
}

.i-explore-video-icon {
    mask: url('~/assets/images/explore/video.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('~/assets/images/explore/video.svg') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
}

.i-explore-ai-icon {
    mask: url('~/assets/images/explore/AI.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('~/assets/images/explore/AI.svg') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
}

.i-explore-mp3-icon {
    mask: url('~/assets/images/explore/MP3.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('~/assets/images/explore/MP3.svg') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
}
</style>
